// Persistence for the confluence board. Owns the SAVED boards, the CURRENT
// board name, the live WORKING card list, and the dirty flag. The rest of the
// system (background refresh, notifications, news, band-scan) still reads
// `watchlistTickers` — a flat symbol list — so this store DERIVES that key from
// the working board's distinct symbols and keeps it in sync. Nothing downstream
// has to change; the board is just a richer front-end over the same tracked set.
//
// Explicit save semantics (fixing the prototype's auto-save bug): edits touch
// `working` only; a saved board changes solely on an explicit save.

import { Emitter, onStorageChange, storageGet, storageSet } from "./storage.js";
import { normalizeBoard, newCard, newId, CAT_FAMILY, type Board, type Card, type ReadingKey, READING_KEYS, TIMEFRAMES } from "./confluence.js";
import { syncLiveSetups } from "./web-push.js";
import { DEFAULT_SECTOR_WATCHLISTS } from "./default-watchlists.js";

const BOARDS_KEY = "wlBoards";
const CURRENT_KEY = "wlCurrent";
const WORKING_KEY = "wlWorking";
const DIRTY_KEY = "wlDirty";
const LIBRARY_KEY = "wlSetupLibrary"; // per-symbol library of SAVED setups (superset of the live working board)
const TICKERS_KEY = "watchlistTickers"; // shared with the rest of the app
const TIMEFRAMES_KEY = "watchlistTimeframes"; // legacy per-symbol tf prefs (for migration)

/** The trade-type key a saved setup belongs to, for the "one live per type per
 * symbol" rule. A card's type is its `name` (matching a TRADE_TYPES entry);
 * an unnamed card is the user's own "Custom" setup, its own bucket. */
export function tradeKey(card: Card): string {
  return card.name && card.name.trim() ? card.name.trim() : "Custom";
}

function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v));
}
/** Coerce any stored/legacy value into a valid Card (ids, in-range fields). */
function sanitizeCard(raw: unknown): Card | null {
  const e = raw as Partial<Card> | null;
  if (!e || typeof e.sym !== "string" || !e.sym) return null;
  const cols = Array.isArray(e.cols) && e.cols.length
    ? e.cols
        .filter((c) => c && READING_KEYS.includes(c.reading as ReadingKey) && typeof c.tf === "string")
        .map((c) => ({ reading: c.reading as ReadingKey, tf: c.tf, ...(typeof c.period === "number" && c.period >= 2 && c.period <= 400 ? { period: Math.round(c.period) } : {}), ...(typeof c.os === "number" && c.os >= 0 && c.os <= 1 ? { os: c.os } : {}), ...(typeof c.ob === "number" && c.ob >= 0 && c.ob <= 1 ? { ob: c.ob } : {}) }))
    : newCard(e.sym).cols;
  // Preserve the per-category reading choice (statistical / structural), keeping
  // only values that belong to that category's family — otherwise sanitizeCard
  // would silently drop it, which is why the field never persisted before.
  const catReading: NonNullable<Card["catReading"]> = {};
  if (e.catReading && typeof e.catReading === "object") {
    for (const c of ["statistical", "structural"] as const) {
      const rk = e.catReading[c];
      if (rk && CAT_FAMILY[c].includes(rk)) catReading[c] = rk;
    }
  }
  // Preserve the per-category lookback (statistical / structural), keeping only
  // sane positive integers — else sanitizeCard would silently drop the field.
  const catLookback: NonNullable<Card["catLookback"]> = {};
  if (e.catLookback && typeof e.catLookback === "object") {
    for (const c of ["statistical", "structural"] as const) {
      const lb = e.catLookback[c];
      if (typeof lb === "number" && lb >= 2 && lb <= 400) catLookback[c] = Math.round(lb);
    }
  }
  const filters: NonNullable<Card["filters"]> = {};
  if (e.filters && typeof e.filters === "object") {
    if (typeof e.filters.volMin === "number" && e.filters.volMin > 0) filters.volMin = e.filters.volMin;
    if (typeof e.filters.volatilityMin === "number" && e.filters.volatilityMin > 0) filters.volatilityMin = e.filters.volatilityMin;
    // Reading gates (D-115): keep only well-formed gates — a known reading, a string
    // timeframe, and at least one of min/max in 0..1 (else the gate is a no-op we drop).
    if (Array.isArray(e.filters.gates)) {
      const gates = e.filters.gates
        .filter((g) => g && READING_KEYS.includes(g.reading as ReadingKey) && typeof g.tf === "string"
          && ((typeof g.min === "number" && g.min >= 0 && g.min <= 1) || (typeof g.max === "number" && g.max >= 0 && g.max <= 1)))
        .map((g) => ({
          reading: g.reading as ReadingKey, tf: g.tf,
          ...(typeof g.min === "number" && g.min >= 0 && g.min <= 1 ? { min: g.min } : {}),
          ...(typeof g.max === "number" && g.max >= 0 && g.max <= 1 ? { max: g.max } : {}),
          ...(typeof g.period === "number" && g.period >= 2 && g.period <= 400 ? { period: Math.round(g.period) } : {}),
          ...(typeof g.lookback === "number" && g.lookback >= 2 && g.lookback <= 400 ? { lookback: Math.round(g.lookback) } : {}),
        }));
      if (gates.length) filters.gates = gates;
    }
  }
  // Preserve the trade-type label + direction (D-114). Absent → the card is the
  // user's "Custom" setup; keeping them optional means old boards load unchanged.
  const name = typeof e.name === "string" && e.name.trim() ? e.name.trim().slice(0, 40) : undefined;
  const label = typeof e.label === "string" && e.label.trim() ? e.label.trim().slice(0, 60) : undefined;
  const dir = e.dir === "buy" || e.dir === "sell" ? e.dir : undefined;
  // Follow/fade mode (D-115); only "follow" is stored — absent means fade, so
  // every legacy card is unchanged.
  const mode = e.mode === "follow" ? "follow" : undefined;
  // Watchlist-scan tag (D-158): a plain marker, preserved so a scan group survives reloads.
  const scan = e.scan === true ? true : undefined;
  // Breakout engine (D-115): keep only a well-formed { tf, lookback, buffer?, confirm? }.
  // Anything malformed falls back to no engine (confluence), so a bad blob can't brick a
  // card. Only kept when engine === "breakout" AND its params validate.
  let engine: Card["engine"] | undefined;
  let breakout: Card["breakout"] | undefined;
  if (e.engine === "breakout" && e.breakout && typeof e.breakout === "object") {
    const b = e.breakout;
    const okTf = typeof b.tf === "string" && b.tf;
    const okLb = typeof b.lookback === "number" && b.lookback >= 2 && b.lookback <= 400;
    if (okTf && okLb) {
      const cf = b.confirm;
      const okConfirm = cf && typeof cf === "object" && READING_KEYS.includes(cf.reading as ReadingKey)
        && typeof cf.tf === "string" && typeof cf.min === "number" && cf.min >= 0 && cf.min <= 1;
      engine = "breakout";
      breakout = {
        tf: b.tf, lookback: Math.round(b.lookback),
        ...(typeof b.buffer === "number" && b.buffer >= 0 && b.buffer <= 1 ? { buffer: b.buffer } : {}),
        ...(okConfirm ? { confirm: { reading: cf!.reading as ReadingKey, tf: cf!.tf, min: cf!.min } } : {}),
      };
    }
  }
  return {
    id: typeof e.id === "string" && e.id ? e.id : newCard(e.sym).id,
    sym: e.sym.toUpperCase(),
    cols: cols.length ? cols : newCard(e.sym).cols,
    rule: e.rule === "k" ? "k" : "all",
    k: typeof e.k === "number" ? e.k : 3,
    os: typeof e.os === "number" ? e.os : 0.3,
    ob: typeof e.ob === "number" ? e.ob : 0.7,
    ...(Object.keys(catReading).length ? { catReading } : {}),
    ...(Object.keys(catLookback).length ? { catLookback } : {}),
    ...(Object.keys(filters).length ? { filters } : {}),
    ...(name ? { name } : {}),
    ...(label ? { label } : {}),
    ...(dir ? { dir } : {}),
    ...(mode ? { mode } : {}),
    ...(engine ? { engine } : {}),
    ...(breakout ? { breakout } : {}),
    ...(scan ? { scan: true } : {}),
  };
}
function sanitizeBoard(raw: unknown): Board {
  if (!Array.isArray(raw)) return [];
  return normalizeBoard(raw.map(sanitizeCard).filter((c): c is Card => c != null));
}

export class ConfluenceStore {
  boards: Record<string, Board> = {};
  current: string | null = null;
  working: Board = [];
  /** Every SAVED setup, across symbols (superset of `working`). A setup is LIVE
   * when its id is also in `working` — the live one per (symbol, trade-type) is
   * what arms the Watch pill. Non-live saved setups live only here. */
  library: Board = [];
  dirty = false;
  readonly changed = new Emitter();
  private tickerSync: string[] = [];

  async init(): Promise<void> {
    const rawBoards = await storageGet<Record<string, unknown>>(BOARDS_KEY, {});
    this.boards = {};
    for (const name of Object.keys(rawBoards)) this.boards[name] = sanitizeBoard(rawBoards[name]);

    this.current = await storageGet<string | null>(CURRENT_KEY, null);
    if (this.current && !this.boards[this.current]) this.current = null;

    const workingRaw = await storageGet<unknown>(WORKING_KEY, null);
    if (workingRaw != null) this.working = sanitizeBoard(workingRaw);
    else if (this.current) this.working = clone(this.boards[this.current]!);
    else this.working = await this.migrateFromLegacy();

    this.dirty = (await storageGet<string>(DIRTY_KEY, "0")) === "1";

    // Saved-setup library (D-118): the user's OWN saved setups per symbol. It does
    // NOT absorb the auto-created default pills — it starts EMPTY on first run and
    // fills only as the user creates or saves setups. A saved setup is LIVE when its
    // id is also in `working` (arming the pill).
    const libRaw = await storageGet<unknown>(LIBRARY_KEY, null);
    this.library = libRaw != null ? sanitizeBoard(libRaw) : [];

    // One-time working-board cleanup (D-118): earlier testing left several cards per
    // symbol (auto-created defaults + materialised trade-type cards), which showed as
    // phantom pill icons and made removal multi-click. Collapse each symbol to a single
    // fresh card — ONCE, guarded by a flag. Never touches the saved-setup library: that
    // holds the user's real saves and must survive reloads.
    const RESET_KEY = "wlReset118";
    if ((await storageGet<string>(RESET_KEY, "0")) !== "1") {
      this.working = this.distinctSyms().map((s) => newCard(s));
      await this.persistWorking();
      await storageSet(RESET_KEY, "1");
    }

    // Default sector watchlists (D-153): one-time seed of the 11 GICS-sector
    // lists (20 largest names each). Runs before ensureActiveWatchlist so it can
    // open straight into a populated list instead of a stray empty Watchlist_1.
    await this.seedDefaultWatchlists();

    // Watchlists model (D-121): there is always exactly one ACTIVE named watchlist.
    // If we loaded an unnamed working board (legacy, or a pre-D-121 blank), wrap it
    // into a fresh Watchlist_N so nothing is lost and every change auto-saves to it.
    await this.ensureActiveWatchlist();

    await this.reconcileTickers(); // absorb externally-added symbols (band-scan) + write derived list
    // Band-scan (service worker) may add symbols to watchlistTickers while the
    // popup is open — mirror those into the board as default cards.
    onStorageChange<string[]>(TICKERS_KEY, () => void this.absorbExternalTickers());

    this.changed.emit();
  }

  /** First run after upgrade: rebuild today's watchlist as a default board so no
   * user loses their list — each ticker becomes Bollinger %B across its visible
   * timeframes (or the six-timeframe default). */
  private async migrateFromLegacy(): Promise<Board> {
    const tickers = await storageGet<string[]>(TICKERS_KEY, []);
    const tfPrefs = await storageGet<Record<string, string[]>>(TIMEFRAMES_KEY, {});
    const board: Board = tickers.map((sym) => {
      const card = newCard(sym);
      const tfs = tfPrefs[sym];
      if (tfs && tfs.length) card.cols = tfs.filter((t) => TIMEFRAMES.includes(t)).map((tf) => ({ reading: "bb" as ReadingKey, tf }));
      if (!card.cols.length) card.cols = newCard(sym).cols;
      return card;
    });
    return normalizeBoard(board);
  }

  distinctSyms(): string[] {
    const out: string[] = [];
    for (const c of this.working) if (!out.includes(c.sym)) out.push(c.sym);
    return out;
  }

  private async reconcileTickers(): Promise<void> {
    const tracked = await storageGet<string[]>(TICKERS_KEY, []);
    let added = false;
    for (const sym of tracked) if (!this.working.some((c) => c.sym === sym)) { this.working.push(newCard(sym)); added = true; }
    if (added) this.working = normalizeBoard(this.working);
    await this.persistWorking();
  }
  private async absorbExternalTickers(): Promise<void> {
    const before = this.distinctSyms().join(",");
    await this.reconcileTickers();
    if (this.distinctSyms().join(",") !== before) this.changed.emit();
  }

  /** Write the working board + derived tracked-symbol list. The ticker write is
   * guarded so our own write can't loop through onStorageChange. Watchlists
   * auto-save (D-121): the active watchlist IS `boards[current]`, so every change
   * to the working board is written straight back to it — no dirty flag, no Save. */
  private async persistWorking(): Promise<void> {
    await storageSet(WORKING_KEY, this.working);
    if (this.current && this.boards[this.current]) {
      this.boards[this.current] = clone(this.working);
      await storageSet(BOARDS_KEY, this.boards);
    }
    await storageSet(DIRTY_KEY, this.dirty ? "1" : "0");
    // Mirror the live setups (the working board) to the server for the D-138 scan
    // (web + opted-in only; no-op otherwise). Fire-and-forget.
    void syncLiveSetups(this.working.map((c) => ({ id: c.id, symbol: c.sym, card: c })));
    const want = this.distinctSyms();
    if (want.length !== this.tickerSync.length || want.some((s, i) => s !== this.tickerSync[i])) {
      this.tickerSync = want;
      const have = await storageGet<string[]>(TICKERS_KEY, []);
      if (want.length !== have.length || want.some((s, i) => s !== have[i])) await storageSet(TICKERS_KEY, want);
    }
  }
  private async persistBoards(): Promise<void> {
    await storageSet(BOARDS_KEY, this.boards);
    await storageSet(CURRENT_KEY, this.current ?? "");
  }
  private async persistLibrary(): Promise<void> {
    await storageSet(LIBRARY_KEY, this.library);
  }

  // ---- saved setups (library + live flag) — D-118 --------------------------

  /** All SAVED setups for a symbol, in library order. */
  setupsForSym(sym: string): Card[] {
    const s = sym.toUpperCase();
    return this.library.filter((c) => c.sym === s);
  }
  /** Is this saved setup LIVE (arming a Watch pill)? */
  isLive(id: string): boolean {
    return this.working.some((c) => c.id === id);
  }
  /** A unique display LABEL within a symbol, appending " copy" / " copy N" (matching
   * the watchlist duplicate convention). Operates on the display name (label ?? name),
   * never the trade-type `name` — so both copies keep the same one-live-per-type rule. */
  private uniqueLabel(sym: string, base: string): string {
    const taken = new Set(this.setupsForSym(sym).map((c) => (c.label ?? c.name ?? "").toLowerCase()));
    let nm = `${base} copy`;
    let i = 1;
    while (taken.has(nm.toLowerCase())) { i++; nm = `${base} copy ${i}`; }
    return nm;
  }
  /** Make a saved setup live or not. Turning one live drops any other live setup
   * of the SAME trade type on that symbol (one live per type arms the pill). */
  async setLive(cardId: string, on: boolean): Promise<void> {
    const card = this.library.find((c) => c.id === cardId);
    if (!card) return;
    if (on) {
      const key = tradeKey(card);
      this.working = this.working.filter((c) => !(c.sym === card.sym && tradeKey(c) === key && c.id !== card.id));
      if (!this.working.some((c) => c.id === card.id)) this.working.push(clone(card));
    } else {
      this.working = this.working.filter((c) => c.id !== card.id);
    }
    await this.setWorking(this.working); // persists working + tickers + emits
  }
  /** Add a new saved setup or update an existing one (by id); keeps the live pill
   * in sync when the setup is live. */
  async upsertSetup(card: Card): Promise<void> {
    const i = this.library.findIndex((c) => c.id === card.id);
    if (i >= 0) this.library[i] = clone(card); else this.library.push(clone(card));
    const wi = this.working.findIndex((c) => c.id === card.id);
    if (wi >= 0) this.working[wi] = clone(card);
    await this.persistLibrary();
    if (wi >= 0) await this.setWorking(this.working); else this.changed.emit();
  }
  /** Duplicate a saved setup (fresh id, "<name> copy" display LABEL, same trade type,
   * NOT live) just below it. The trade-type `name` is unchanged so both copies share
   * the one-live-per-type rule (only one of the same type can arm the pill). */
  async duplicateSetup(id: string): Promise<string | null> {
    const i = this.library.findIndex((c) => c.id === id);
    if (i < 0) return null;
    const src = this.library[i]!;
    const copy = clone(src);
    copy.id = newId();
    copy.label = this.uniqueLabel(src.sym, src.label ?? src.name ?? "unnamed");
    this.library.splice(i + 1, 0, copy);
    await this.persistLibrary();
    this.changed.emit();
    return copy.id;
  }
  /** Remove a security entirely from Watch — every live card AND every saved setup for
   * that symbol (removing the card removes the whole security, not one setup at a time). */
  async removeSymbol(rawSym: string): Promise<void> {
    const sym = rawSym.toUpperCase();
    this.library = this.library.filter((c) => c.sym !== sym);
    await this.persistLibrary();
    this.working = this.working.filter((c) => c.sym !== sym);
    await this.setWorking(this.working); // persists working + derived tickers + emits
  }
  /** Delete a saved setup; also unlights its pill if it was live. */
  async deleteSetup(id: string): Promise<void> {
    if (!this.library.some((c) => c.id === id)) return;
    this.library = this.library.filter((c) => c.id !== id);
    const wasLive = this.working.some((c) => c.id === id);
    this.working = this.working.filter((c) => c.id !== id);
    await this.persistLibrary();
    if (wasLive) await this.setWorking(this.working); else this.changed.emit();
  }

  // ---- watchlist-wide scan (D-158) -----------------------------------------

  /** Fan a built setup out LIVE across every security in the active watchlist.
   * Each security gets its own clone (fresh id, tagged `scan: true`), replacing
   * any existing live setup of the SAME trade-type on that symbol (the one-live-
   * per-type rule) — so the normal scan + Monitor-notification path fires per
   * security exactly as an individually-armed setup does. */
  async applySetupToAll(template: Card): Promise<void> {
    const key = tradeKey(template);
    for (const sym of this.distinctSyms()) {
      this.working = this.working.filter((c) => !(c.sym === sym && tradeKey(c) === key));
      const copy = clone(template);
      copy.id = newId();
      copy.sym = sym;
      copy.scan = true;
      this.working.push(copy);
    }
    await this.setWorking(this.working); // persists working + derived tickers + mirrors live setups + emits
  }
  /** Any watchlist-scan setups currently armed on the working board? */
  hasScanSetups(): boolean { return this.working.some((c) => c.scan); }
  /** Remove every watchlist-scan setup from the working board in one action. */
  async clearScanSetups(): Promise<void> {
    if (!this.working.some((c) => c.scan)) return;
    this.working = this.working.filter((c) => !c.scan);
    await this.setWorking(this.working);
  }

  uniqueName(base: string): string {
    if (!this.boards[base]) return base;
    let i = 2;
    while (this.boards[`${base} ${i}`]) i++;
    return `${base} ${i}`;
  }

  // ---- mutations (used by the UI) ------------------------------------------

  /** Replace the working cards (already-edited copy); marks dirty unless told otherwise. */
  async setWorking(cards: Board, markDirty = true): Promise<void> {
    this.working = normalizeBoard(cards);
    this.dirty = markDirty;
    await this.persistWorking();
    this.changed.emit();
  }
  async addSecurity(rawSym: string): Promise<void> {
    const sym = rawSym.trim().toUpperCase();
    if (!sym) return;
    this.working.push(newCard(sym));
    await this.setWorking(this.working);
  }
  async saveAsNew(name: string): Promise<string> {
    const nm = this.uniqueName(name.trim());
    this.boards[nm] = clone(this.working);
    this.current = nm;
    this.dirty = false;
    await this.persistBoards();
    await this.persistWorking();
    this.changed.emit();
    return nm;
  }
  async saveCurrent(): Promise<void> {
    if (!this.current) return;
    this.boards[this.current] = clone(this.working);
    this.dirty = false;
    await this.persistBoards();
    await this.persistWorking();
    this.changed.emit();
  }
  async switchTo(name: string): Promise<void> {
    if (!this.boards[name]) return;
    this.working = clone(this.boards[name]!);
    this.current = name;
    this.dirty = false;
    await this.persistBoards();
    await this.persistWorking();
    this.changed.emit();
  }
  // ---- watchlists (D-121) --------------------------------------------------

  /** The next free default watchlist name, "Watchlist_N" (lowest unused N). */
  nextWatchlistName(): string {
    let i = 1;
    while (this.boards[`Watchlist_${i}`]) i++;
    return `Watchlist_${i}`;
  }
  /** One-time seed of the default GICS-sector watchlists (D-153). Creates one
   * board per sector, each pre-filled with its ~20 largest names, and opens into
   * the first sector. Guarded by a flag so it runs exactly once and never
   * overwrites a user's own edits, renames, or deletions afterwards. A sector
   * name that already exists is skipped (never clobbered). */
  private async seedDefaultWatchlists(): Promise<void> {
    const SEED_KEY = "wlSeedSectors1";
    if ((await storageGet<string>(SEED_KEY, "0")) === "1") return;

    const seeded: Record<string, Board> = {};
    for (const { name, tickers } of DEFAULT_SECTOR_WATCHLISTS) {
      if (this.boards[name]) continue; // don't clobber an existing same-named list
      seeded[name] = normalizeBoard(tickers.map((s) => newCard(s)));
    }
    // Put the sector lists first, keeping any pre-existing boards after them.
    this.boards = { ...seeded, ...this.boards };

    // Open straight into the first sector when there's no meaningful active list
    // yet — i.e. nothing selected, or only a stray empty auto-named Watchlist_N.
    const firstSector = DEFAULT_SECTOR_WATCHLISTS[0]?.name;
    if (firstSector && this.boards[firstSector]) {
      const cur = this.current;
      const curIsEmptyDefault = !!cur && /^Watchlist_\d+$/.test(cur) && (this.boards[cur]?.length ?? 0) === 0;
      if (!cur || curIsEmptyDefault) {
        if (curIsEmptyDefault && cur) delete this.boards[cur];
        this.current = firstSector;
        this.working = clone(this.boards[firstSector]!);
      }
    }

    await storageSet(SEED_KEY, "1");
    await this.persistBoards();
    await this.persistWorking();
  }

  /** Guarantee exactly one ACTIVE named watchlist. Wraps an unnamed working board
   * into a fresh Watchlist_N (keeping its cards), or spins up an empty one on a
   * truly first run. Persists. */
  async ensureActiveWatchlist(): Promise<void> {
    if (this.current && this.boards[this.current]) return;
    const nm = this.nextWatchlistName();
    this.boards[nm] = clone(this.working); // may be [] on first run — empty is fine
    this.current = nm;
    this.dirty = false;
    await this.persistBoards();
    await this.persistWorking();
  }
  /** Create a new EMPTY watchlist named Watchlist_N and make it active. Returns
   * the name. Auto-saves — there is no separate Save step. */
  async newWatchlist(): Promise<string> {
    const nm = this.nextWatchlistName();
    this.boards[nm] = [];
    this.current = nm;
    this.working = [];
    this.dirty = false;
    await this.persistBoards();
    await this.persistWorking();
    this.changed.emit();
    return nm;
  }
  async renameBoard(name: string, to: string): Promise<void> {
    if (!this.boards[name]) return;
    const nn = this.uniqueName(to.trim());
    this.boards[nn] = this.boards[name]!;
    delete this.boards[name];
    if (this.current === name) this.current = nn;
    await this.persistBoards();
    this.changed.emit();
  }
  /** Duplicate a saved board under a fresh name; returns the new name (or null
   * if the source board is missing). */
  async duplicateBoard(name: string): Promise<string | null> {
    if (!this.boards[name]) return null;
    const copy = this.uniqueName(`${name} copy`);
    this.boards[copy] = clone(this.boards[name]!);
    await this.persistBoards();
    this.changed.emit();
    return copy;
  }
  /** Manually reorder watchlists: move `name` to just before/after `target`.
   * Watchlists render in `boards` key order, so we rebuild the object in the new
   * order (D-121). */
  async reorderBoard(name: string, target: string, after: boolean): Promise<void> {
    if (!this.boards[name] || !this.boards[target] || name === target) return;
    const order = Object.keys(this.boards).filter((n) => n !== name);
    const ti = order.indexOf(target);
    order.splice(after ? ti + 1 : ti, 0, name);
    const rebuilt: Record<string, Board> = {};
    for (const n of order) rebuilt[n] = this.boards[n]!;
    this.boards = rebuilt;
    await this.persistBoards();
    this.changed.emit();
  }
  async deleteBoard(name: string): Promise<void> {
    if (!this.boards[name]) return;
    delete this.boards[name];
    // There must always be one active watchlist. If we deleted the active one,
    // fall through to another; if none remain, spin up a fresh empty one.
    if (this.current === name) {
      const next = Object.keys(this.boards)[0] ?? null;
      if (next) {
        this.current = next;
        this.working = clone(this.boards[next]!);
        await this.persistBoards();
        await this.persistWorking();
      } else {
        this.current = null;
        await this.newWatchlist(); // creates Watchlist_1, sets current/working, persists
        return;
      }
    } else {
      await this.persistBoards();
    }
    this.changed.emit();
  }
}

export const confluenceStore = new ConfluenceStore();
